<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '@yuksalish/auth'
import { formatApiError } from '@yuksalish/api-client'
import { formatDisplayDateTime } from '@yuksalish/ui'
import { usePlanningApi } from '../composables/usePlanningApi'
import { SHIFT_VALUES } from '../types/planning'
import type { MachineAssignment, ProductionOrder } from '../types/planning'

/**
 * Shift planning: who and what is covered in a shift.
 *
 * The backend has no shift roster model, so coverage is derived from the real
 * shift assignments (`/production-execution/assignments?shift=…`), the machine
 * state (`/production-execution/machines`) and the released orders
 * (`/production-orders?status=in_progress`). Gaps are therefore "a released
 * order has no machine or no operator assigned for this shift", which is a real
 * signal, not a staffing standard the backend does not have.
 */

const { t } = useI18n()
const auth = useAuthStore()
const { listAllProductionOrders, listMachines, listAllAssignments, endAssignment } = usePlanningApi()

const permissions = computed(() => auth.user?.permissions ?? [])
const canManage = computed(() => permissions.value.includes('production_orders.manage'))

const shift = ref<string>(SHIFT_VALUES[0])
const orders = ref<ProductionOrder[]>([])
const assignments = ref<MachineAssignment[]>([])
const machineStatuses = ref<Array<{ id: number; name: string; status: string; fault_active: boolean }>>([])
const loading = ref(true)
const error = ref('')
const actionError = ref('')
const actionMessage = ref('')
const busyId = ref<number | null>(null)
const filter = ref('')

const activeAssignments = computed(() => assignments.value.filter((entry) => entry.status === 'active'))

const machineAssignments = computed(() => activeAssignments.value.filter((entry) => entry.type === 'machine'))
const employeeAssignments = computed(() =>
  activeAssignments.value.filter((entry) => entry.type === 'employee'),
)

const coveredOrderIds = computed(
  () => new Set(activeAssignments.value.map((entry) => String(entry.order_id))),
)

const machineCoveredOrderIds = computed(
  () => new Set(machineAssignments.value.map((entry) => String(entry.order_id))),
)

/** Released orders with no machine booked for this shift. */
const ordersWithoutMachine = computed(() =>
  orders.value.filter((order) => !machineCoveredOrderIds.value.has(String(order.id))),
)

const ordersWithoutAnyAssignment = computed(() =>
  orders.value.filter((order) => !coveredOrderIds.value.has(String(order.id))),
)

const runningMachines = computed(
  () => machineStatuses.value.filter((machine) => machine.status === 'running').length,
)
const problemMachines = computed(
  () =>
    machineStatuses.value.filter(
      (machine) => machine.status === 'down' || machine.status === 'setup' || machine.fault_active,
    ).length,
)

/** One row per order/stage that has coverage in this shift. */
interface CoverageRow {
  key: string
  orderCode: string
  stage: string
  machines: string[]
  employees: string[]
  lotNumbers: string[]
  assignedAt: string
}

const coverageRows = computed<CoverageRow[]>(() => {
  const grouped = new Map<string, CoverageRow>()
  for (const entry of activeAssignments.value) {
    const key = `${entry.order_id}-${entry.stage_code}`
    const row = grouped.get(key) ?? {
      key,
      orderCode: entry.order_code ?? `#${entry.order_id}`,
      stage: entry.stage_name || entry.stage_code,
      machines: [],
      employees: [],
      lotNumbers: [],
      assignedAt: entry.assigned_at,
    }
    if (entry.type === 'machine' && entry.machine_name) row.machines.push(entry.machine_name)
    if (entry.type === 'employee' && entry.employee_name) row.employees.push(entry.employee_name)
    if (entry.lot_number && !row.lotNumbers.includes(entry.lot_number)) row.lotNumbers.push(entry.lot_number)
    grouped.set(key, row)
  }
  const term = filter.value.trim().toLowerCase()
  return [...grouped.values()]
    .filter((row) =>
      !term
        ? true
        : [row.orderCode, row.stage, ...row.machines, ...row.employees]
            .join(' ')
            .toLowerCase()
            .includes(term),
    )
    .sort((a, b) => a.orderCode.localeCompare(b.orderCode))
})

async function load() {
  loading.value = true
  error.value = ''
  actionError.value = ''
  try {
    const [orderList, machineList, assignmentList] = await Promise.all([
      listAllProductionOrders({ status: 'in_progress' }),
      listMachines(),
      listAllAssignments({ shift: shift.value }),
    ])
    orders.value = orderList
    machineStatuses.value = machineList
    assignments.value = assignmentList
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

/** Ending an assignment frees the machine or operator for the shift. */
async function release(entry: MachineAssignment) {
  actionError.value = ''
  actionMessage.value = ''
  if (!window.confirm(t('planning.shifts.releaseConfirm'))) return
  busyId.value = entry.id
  try {
    await endAssignment(entry.id)
    actionMessage.value = t('planning.shifts.released')
    await load()
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    busyId.value = null
  }
}

onMounted(() => {
  void load()
})
</script>

<template>
  <div class="page-heading">
    <div>
      <p class="eyebrow">{{ t('planning.shifts.eyebrow') }}</p>
      <h2>{{ t('planning.shifts.title') }}</h2>
      <p>{{ t('planning.shifts.description') }}</p>
    </div>
  </div>

  <div class="toolbar">
    <select v-model="shift" class="filter-select" @change="load">
      <option v-for="value in SHIFT_VALUES" :key="value" :value="value">
        {{ t(`planning.shift.${value}`) }}
      </option>
    </select>
    <button class="btn-secondary" :disabled="loading" @click="load">
      {{ t('planning.common.refresh') }}
    </button>
  </div>

  <p v-if="actionError" class="error-inline">{{ actionError }}</p>
  <p v-if="actionMessage" class="action-message">{{ actionMessage }}</p>

  <div v-if="loading" class="state-message"><p>{{ t('planning.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('planning.common.retry') }}</button>
  </div>

  <template v-else>
    <div class="summary-row">
      <div class="summary-card">
        <span class="summary-label">{{ t('planning.shifts.machinesAssigned') }}</span>
        <span class="summary-value">{{ machineAssignments.length }}</span>
        <span class="summary-label">
          {{ t('planning.shifts.running') }}: {{ runningMachines }} / {{ machineStatuses.length }}
        </span>
      </div>
      <div class="summary-card">
        <span class="summary-label">{{ t('planning.shifts.operatorsAssigned') }}</span>
        <span class="summary-value">{{ employeeAssignments.length }}</span>
        <span class="summary-label">{{ t('planning.shifts.activeAssignments', { count: activeAssignments.length }) }}</span>
      </div>
      <div class="summary-card" :class="{ attention: ordersWithoutMachine.length > 0 }">
        <span class="summary-label">{{ t('planning.shifts.ordersWithoutMachine') }}</span>
        <span class="summary-value">{{ ordersWithoutMachine.length }}</span>
        <span class="summary-label">
          {{ t('planning.shifts.releasedOrders') }}: {{ orders.length }}
        </span>
      </div>
      <div class="summary-card" :class="{ attention: problemMachines > 0 }">
        <span class="summary-label">{{ t('planning.shifts.machineIssues') }}</span>
        <span class="summary-value">{{ problemMachines }}</span>
        <span class="summary-label">{{ t('planning.shifts.machineIssuesHint') }}</span>
      </div>
    </div>

    <section v-if="ordersWithoutAnyAssignment.length" class="panel" style="margin-bottom: 18px">
      <div class="panel-header">
        <h3>{{ t('planning.shifts.ordersWithoutAssignment') }}</h3>
      </div>
      <table class="data-table compact">
        <thead>
          <tr>
            <th>{{ t('planning.common.order') }}</th>
            <th>{{ t('planning.common.product') }}</th>
            <th>{{ t('planning.common.department') }}</th>
            <th>{{ t('planning.common.startDate') }}</th>
            <th class="cell-num">{{ t('planning.common.planned') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="order in ordersWithoutAnyAssignment" :key="order.id">
            <td class="cell-code">{{ order.mo_code }}</td>
            <td class="cell-name">{{ order.product_name || '—' }}</td>
            <td>{{ order.department_name || '—' }}</td>
            <td class="cell-date">{{ order.start_date || '—' }}</td>
            <td class="cell-num">{{ order.planned_quantity.toLocaleString() }}</td>
          </tr>
        </tbody>
      </table>
    </section>

    <section class="panel">
      <div class="panel-header">
        <h3>{{ t('planning.shifts.coverage') }}</h3>
        <input
          v-model="filter"
          class="search-input"
          type="search"
          :placeholder="t('planning.common.searchPlaceholder')"
        >
      </div>

      <div v-if="coverageRows.length === 0" class="empty-inline">
        {{ t('planning.shifts.noCoverage') }}
      </div>

      <div v-else class="table-scroll">
        <table class="data-table compact">
          <thead>
            <tr>
              <th>{{ t('planning.common.order') }}</th>
              <th>{{ t('planning.common.stage') }}</th>
              <th>{{ t('planning.shifts.machines') }}</th>
              <th>{{ t('planning.shifts.operators') }}</th>
              <th>{{ t('planning.common.lot') }}</th>
              <th>{{ t('planning.common.assignedAt') }}</th>
              <th class="cell-actions">{{ t('planning.common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="row in coverageRows" :key="row.key">
              <td class="cell-code">{{ row.orderCode }}</td>
              <td class="cell-name">{{ row.stage }}</td>
              <td>
                <span v-if="row.machines.length">{{ row.machines.join(', ') }}</span>
                <span v-else class="status-badge status-critical">{{ t('planning.shifts.noMachine') }}</span>
              </td>
              <td>
                <span v-if="row.employees.length">{{ row.employees.join(', ') }}</span>
                <span v-else class="empty-inline">—</span>
              </td>
              <td>{{ row.lotNumbers.join(', ') || '—' }}</td>
              <td class="cell-date">{{ formatDisplayDateTime(row.assignedAt) }}</td>
              <td class="cell-actions">
                <span v-if="!canManage" class="empty-inline">—</span>
                <span v-else class="empty-inline">{{ t('planning.shifts.releaseHint') }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="panel" style="margin-top: 18px">
      <div class="panel-header">
        <h3>{{ t('planning.shifts.activeAssignmentList') }}</h3>
      </div>
      <div v-if="activeAssignments.length === 0" class="empty-inline">
        {{ t('planning.shifts.noAssignments') }}
      </div>
      <div v-else class="table-scroll">
        <table class="data-table compact">
          <thead>
            <tr>
              <th>{{ t('planning.common.type') }}</th>
              <th>{{ t('planning.common.order') }}</th>
              <th>{{ t('planning.common.stage') }}</th>
              <th>{{ t('planning.shifts.resource') }}</th>
              <th>{{ t('planning.common.lot') }}</th>
              <th>{{ t('planning.common.assignedAt') }}</th>
              <th class="cell-actions">{{ t('planning.common.actions') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="entry in activeAssignments" :key="entry.id">
              <td>
                <span class="status-badge status-neutral">
                  {{ entry.type === 'machine' ? t('planning.common.machine') : t('planning.common.operator') }}
                </span>
              </td>
              <td class="cell-code">{{ entry.order_code || `#${entry.order_id}` }}</td>
              <td>{{ entry.stage_name || entry.stage_code }}</td>
              <td class="cell-name">{{ entry.machine_name || entry.employee_name || '—' }}</td>
              <td>{{ entry.lot_number || '—' }}</td>
              <td class="cell-date">{{ formatDisplayDateTime(entry.assigned_at) }}</td>
              <td class="cell-actions">
                <button
                  v-if="canManage"
                  class="link-action danger"
                  :disabled="busyId === entry.id"
                  @click="release(entry)"
                >
                  {{ t('planning.shifts.release') }}
                </button>
                <span v-else class="empty-inline">—</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="empty-inline">{{ t('planning.shifts.gapHint') }}</p>
    </section>
  </template>
</template>
